import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Routes as AppRoutes } from "../../../routes";
import BundleSelector, {
  isHiddenCreateOrderBundle,
} from "../../../components/va/BundleSelector";
import DistributorSelect from "../../../components/va/DistributorSelect";
import OrderHospitalSelect from "../../../components/va/OrderHospitalSelect";
import PrescriptionSection from "../../../components/va/PrescriptionSection";
import PrescriptionConcernsBanner from "../../../components/va/PrescriptionConcernsBanner";
import PoAddressIssuesBanner from "../../../components/va/PoAddressIssuesBanner";
import { getPoAddressIssues } from "../../../helpers/poAddressValidation";
import { buildReviewPrescriptionConcerns } from "../../../helpers/vaPrescriptionConcerns";
import DeliverySummary, {
  getDeliveryFieldErrors,
} from "../../../components/va/DeliverySummary";
import Button from "../../../components/shared/buttons/button";
import Loader from "../../../components/shared/loader/Loader";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { useConfirmVaOrder } from "../../../hooks/useConfirmVaOrder";
import { useParsePrescription } from "../../../hooks/useParsePrescription";
import {
  useGetVaDistributorsQuery,
  useGetVaHospitalsQuery,
  useGetVaBundleQuery,
} from "../../../app/vaApi/vaApi";
import {
  useCreateVaDeviceOrderMutation,
  useGetodersByFilterMutation,
  useUpdateOrderByShopifyItemIDMutation,
} from "../../../app/orderApi/OrderApi";
import {
  MOCK_DISTRIBUTOR_OPTIONS,
  bundleRequiresPrescription,
} from "../../../mocks/va/mockData";
import {
  getParseResult,
  saveParseResult,
  getSavedPrescriptionFile,
  savePrescriptionFile,
  clearPrescriptionFile,
  getSavedOriginalPoFile,
  saveOriginalPoFile,
  clearOriginalPoFile as clearStoredOriginalPoFile,
  getSavedInvoiceFile,
  clearInvoiceFile,
} from "../../../helpers/vaParseStorage";
import InvoicePreviewPane from "../../../components/va/InvoicePreviewPane";
import { CancelSvg } from "../../../svgFiles/CancelSvg";
import {
  mapDistributorOption,
  hasLowConfidence,
  getConfirmNavigationOrderId,
  getPoNumber,
  enrichDeliveryName,
  formatDistributorLabel,
  filterDistributorsByState,
} from "../../../helpers/vaInvoiceUtils";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import VaFileInput from "../../../components/va/VaFileInput";
import {
  PRESCRIPTION_UPLOAD_ACCEPT,
  getPrescriptionUploadError,
} from "../../../helpers/prescriptionFormat";
import { showToast } from "../../../helpers/Utils";
import { hasBlockingPrescriptionIssue } from "../../../helpers/prescriptionExtract";
import "../../../styles/vaOrderModal.css";

const isFieldAttachedWarning = (warning) =>
  /no distributor|multiple distributors|facility name|select hospital|multiple hospitals|near hospital/i.test(
    String(warning)
  );

const EMPTY_PRESCRIPTION = {
  od: {},
  os: {},
  pdType: null,
  fileUrl: null,
  fileName: null,
  extractMeta: null,
  prescriptionFileKey: null,
  multiplePrescriptions: false,
  singularPd: null,
  formValues: null,
};

export default function CreateOrderReviewPage({
  asModal = false,
  onClose,
  manual: manualProp,
  initialPrescription,
  hidePrescriptionSection = false,
  initialLensSelection,
  deviceDraft,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isManual =
    manualProp ||
    location.pathname === AppRoutes.CreateOrderManual.path ||
    searchParams.get("create") === "manual";
  const parseId = searchParams.get("parseId");
  const skuCode = searchParams.get("sku");

  const isDeviceOrder = Boolean(deviceDraft);
  const parseData = useMemo(() => {
    if (deviceDraft) return { delivery: {}, prescription: EMPTY_PRESCRIPTION };
    if (isManual) return null;
    return location.state?.parse || getParseResult(parseId);
  }, [deviceDraft, isManual, location.state?.parse, parseId]);

  const { data: distributorsFallback } = useGetVaDistributorsQuery(
    { page: 1, limit: 200 },
    { skip: isManual }
  );
  // Full catalog grouped by state — same source as Add VA Distributor.
  const { data: hospitalsData, isFetching: hospitalsLoading } =
    useGetVaHospitalsQuery(undefined, { skip: isManual });

  const hospitalStateGroups = useMemo(
    () => (Array.isArray(hospitalsData?.data) ? hospitalsData.data : []),
    [hospitalsData]
  );

  const hospitalLookup = useMemo(() => {
    const map = new Map();
    for (const group of hospitalStateGroups) {
      for (const hospital of group.hospitals || []) {
        map.set(String(hospital.id), {
          ...hospital,
          stateCode: group.stateCode,
          stateName: group.stateName,
        });
      }
    }
    return map;
  }, [hospitalStateGroups]);

  const [bundleCode, setBundleCode] = useState("CAP1");
  const [distributorId, setDistributorId] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [suggestedHospitals, setSuggestedHospitals] = useState([]);
  const [suggestedDistributors, setSuggestedDistributors] = useState([]);
  const [allDistributorOptions, setAllDistributorOptions] = useState([]);
  const [prescription, setPrescription] = useState(EMPTY_PRESCRIPTION);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [delivery, setDelivery] = useState({});
  const [poNumber, setPoNumber] = useState("");
  const [poEmail, setPoEmail] = useState("");
  const [patientName, setPatientName] = useState("");
  const [providerName, setProviderName] = useState("");
  const [originalPoFile, setOriginalPoFile] = useState(null);
  const [originalPoFileUrl, setOriginalPoFileUrl] = useState("");
  const [distributorOptions, setDistributorOptions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [aiMeta, setAiMeta] = useState({});
  const [selectedDistributorReason, setSelectedDistributorReason] = useState("");
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [rxParseError, setRxParseError] = useState("");
  const [needsManualHospital, setNeedsManualHospital] = useState(false);
  const [showAiExtractConfirmModal, setShowAiExtractConfirmModal] =
    useState(false);
  const contactEmailSourceDistributorRef = useRef("");
  const { confirm, isSubmitting: isConfirmingPo } = useConfirmVaOrder();
  const [createDeviceOrder, { isLoading: isCreatingDevice }] = useCreateVaDeviceOrderMutation();
  const isSubmitting = isConfirmingPo || isCreatingDevice;
  const { parse: parseRx, isParsing: isParsingRx } = useParsePrescription();
  const [getOrders] = useGetodersByFilterMutation();
  const [updateOrder] = useUpdateOrderByShopifyItemIDMutation();

  const clearOriginalPoFile = () => {
    if (parseId) clearStoredOriginalPoFile(parseId);
    setOriginalPoFile(null);
    setOriginalPoFileUrl((current) => {
      if (typeof current === "string" && current.startsWith("blob:")) {
        URL.revokeObjectURL(current);
      }
      return "";
    });
  };

  const handleOriginalPoFileSelect = (file, err) => {
    if (err) {
      showToast(err, "error");
      clearOriginalPoFile();
      return;
    }
    if (!file) {
      clearOriginalPoFile();
      return;
    }
    const error = getPrescriptionUploadError(file);
    if (error) {
      showToast(error, "error");
      clearOriginalPoFile();
      return;
    }
    if (parseId) saveOriginalPoFile(parseId, file);
    const saved = parseId ? getSavedOriginalPoFile(parseId) : null;
    const fileUrl = saved?.fileUrl || URL.createObjectURL(file);
    setOriginalPoFile(saved?.file || file);
    setOriginalPoFileUrl((current) => {
      if (
        typeof current === "string" &&
        current.startsWith("blob:") &&
        current !== fileUrl
      ) {
        URL.revokeObjectURL(current);
      }
      return fileUrl;
    });
  };

  useEffect(() => {
    return () => {
      if (
        typeof originalPoFileUrl !== "string" ||
        !originalPoFileUrl.startsWith("blob:")
      ) {
        return;
      }
      // Storage owns blob URLs saved for this parseId — don't revoke those here.
      const storedUrl = getSavedOriginalPoFile(parseId)?.fileUrl;
      if (storedUrl && storedUrl === originalPoFileUrl) return;
      URL.revokeObjectURL(originalPoFileUrl);
    };
  }, [originalPoFileUrl, parseId]);

  useEffect(() => {
    const saved = parseId ? getSavedOriginalPoFile(parseId) : null;
    if (saved?.file) {
      setOriginalPoFile(saved.file);
      setOriginalPoFileUrl(saved.fileUrl || "");
      return;
    }
    setOriginalPoFile(null);
    setOriginalPoFileUrl("");
  }, [parseId]);

  const handleClose = () => {
    if (parseId) {
      clearPrescriptionFile(parseId);
      clearStoredOriginalPoFile(parseId);
      clearInvoiceFile(parseId);
    }
    if (onClose) {
      onClose();
      return;
    }
    navigate(AppRoutes.Orders.path);
  };

  const savedInvoice = !isManual && parseId ? getSavedInvoiceFile(parseId) : null;
  const hasInvoicePreview = Boolean(asModal && savedInvoice?.fileUrl);

  useEffect(() => {
    if (isManual) {
      const bundleParam = searchParams.get("bundle");
      const initialBundle = bundleParam
        ? String(bundleParam).trim().toUpperCase()
        : "CAP1";
      setBundleCode(
        isHiddenCreateOrderBundle(initialBundle) ? "CAP1" : initialBundle
      );
      setDistributorId("");
      setHospitalId("");
      setFacilityName("");
      setSuggestedHospitals([]);
      setSuggestedDistributors([]);
      setAllDistributorOptions(MOCK_DISTRIBUTOR_OPTIONS);
      setPrescription(initialPrescription ?? EMPTY_PRESCRIPTION);
      setPrescriptionFile(null);
      setDelivery({});
      setDistributorOptions(MOCK_DISTRIBUTOR_OPTIONS);
      setPatientName("");
      setProviderName("");
      setWarnings([]);
      setAiMeta({});
      setSelectedDistributorReason("");
      setNeedsManualHospital(false);
      return;
    }

    if ((!parseId && !isDeviceOrder) || !parseData) {
      if (asModal) {
        setSearchParams({ create: "upload" });
      } else {
        navigate(AppRoutes.CreateOrderUpload.path, { replace: true });
      }
      return;
    }

    {
      const parsedBundle = parseData.bundle?.code || "CAP1";
      setBundleCode(
        isHiddenCreateOrderBundle(parsedBundle) ? "CAP1" : parsedBundle
      );
    }
    setDistributorId(parseData.distributor?.id || "");
    setHospitalId(
      parseData.hospital?.id ||
        parseData.delivery?.hospitalId ||
        ""
    );
    setFacilityName(parseData.facilityName || "");
    setNeedsManualHospital(parseData.needsManualHospital === true);
    setSuggestedHospitals(
      Array.isArray(parseData.suggestedHospitals)
        ? parseData.suggestedHospitals
        : []
    );
    setSuggestedDistributors(
      Array.isArray(parseData.suggestedDistributors)
        ? parseData.suggestedDistributors
        : []
    );

    // Merge prescription from parse + file uploaded in Upload Invoice modal
    const basePrescription = {
      ...EMPTY_PRESCRIPTION,
      ...parseData.prescription,
    };
    const savedRxFile = getSavedPrescriptionFile(parseId);
    if (savedRxFile?.name) {
      basePrescription.fileName = savedRxFile.name;
      basePrescription.fileUrl = savedRxFile.fileUrl || null;
      setPrescriptionFile(savedRxFile.file || null);
    } else {
      setPrescriptionFile(null);
    }
    setPrescription(basePrescription);

    setDelivery(
      enrichDeliveryName(
        parseData.delivery || {},
        parseData.invoiceMeta?.audiologistName
      )
    );
    setPoEmail(
      String(
        parseData.distributor?.email ||
          parseData.invoiceMeta?.poEmail ||
          ""
      ).trim()
    );
    // Patient name = Veteran (same value used as order customerName).
    // Check invoiceMeta and top-level parse payload — never SHIP TO / audiologist.
    const resolvedPatient = String(
      parseData.invoiceMeta?.veteran || parseData.veteran || ""
    ).trim();
    const resolvedProvider = String(
      parseData.invoiceMeta?.provider || parseData.provider || ""
    ).trim();
    setPatientName(resolvedPatient);
    setProviderName(resolvedProvider);
    setWarnings(parseData.warnings || []);
    setAiMeta(parseData.aiMeta || {});
    setSelectedDistributorReason(parseData.distributor?.aiReason || "");
    const parsedDistributors = parseData.distributorOptions || [];
    setAllDistributorOptions(parsedDistributors);
    setDistributorOptions(parsedDistributors);
    setRxParseError(parseData.rxParseError || "");
  }, [
    isManual,
    isDeviceOrder,
    parseId,
    parseData,
    navigate,
    asModal,
    setSearchParams,
    searchParams,
    initialPrescription,
  ]);

  // Hydrate PO number: prefer Upload PO modal value, else parsed invoice.
  const parsedPoNumber = getPoNumber(parseData?.invoiceMeta);
  const uploadPoNumber = String(parseData?.uploadPoNumber || "").trim();

  useEffect(() => {
    setPoNumber(isManual ? "" : uploadPoNumber || parsedPoNumber);
  }, [isManual, parseId, uploadPoNumber, parsedPoNumber]);

  useEffect(() => {
    if (isManual) return;
    const items = distributorsFallback?.data?.items ?? [];
    if (!items.length) return;
    const mapped = items.map(mapDistributorOption).filter(Boolean);
    // Always keep the complete admin list for manual override.
    setAllDistributorOptions(mapped);
    setDistributorOptions(mapped);
  }, [isManual, distributorsFallback]);

  // When hospital is chosen: suggest VAs by hospital state (territories);
  // auto-select if exactly one. Dropdown stays the full list for override.
  useEffect(() => {
    if (isManual) return;

    const hospitalState = hospitalId
      ? String(hospitalLookup.get(String(hospitalId))?.stateCode || "")
          .trim()
          .toUpperCase()
      : "";

    if (!hospitalId) {
      // No hospital yet — keep parse-time suggestions (state-based).
      const fromParse = Array.isArray(parseData?.suggestedDistributors)
        ? parseData.suggestedDistributors
        : [];
      if (fromParse.length) {
        setSuggestedDistributors(fromParse);
      } else if (suggestedHospitals.length && allDistributorOptions.length) {
        const states = suggestedHospitals
          .map((h) => String(h.stateCode || "").trim().toUpperCase())
          .filter(Boolean);
        const covering = allDistributorOptions.filter((d) =>
          (d.territories || [])
            .map((t) => String(t || "").trim().toUpperCase())
            .some((t) => states.includes(t))
        );
        setSuggestedDistributors(
          covering.map((d) => ({ ...d, suggested: true }))
        );
      }
      return;
    }

    const filtered = filterDistributorsByState(
      allDistributorOptions,
      hospitalState
    );
    setSuggestedDistributors(
      filtered.map((d) => ({ ...d, suggested: true }))
    );

    const currentCoversState =
      Boolean(distributorId) &&
      filtered.some((d) => String(d.id) === String(distributorId));

    if (filtered.length === 0) {
      if (distributorId) {
        setDistributorId("");
        setSelectedDistributorReason("");
      }
      return;
    }

    if (filtered.length === 1) {
      const onlyId = String(filtered[0].id);
      if (String(distributorId || "") !== onlyId) {
        setDistributorId(onlyId);
      }
      return;
    }

    // Multiple covering VAs — keep a valid selection; otherwise leave empty.
    if (distributorId && !currentCoversState) {
      setDistributorId("");
      setSelectedDistributorReason("");
    }
  }, [hospitalId, hospitalLookup, allDistributorOptions, isManual, suggestedHospitals, parseData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!distributorId) {
      contactEmailSourceDistributorRef.current = "";
      setPoEmail("");
      setErrors((current) =>
        current.poEmail ? { ...current, poEmail: "" } : current
      );
      return;
    }
    const match =
      allDistributorOptions.find((d) => String(d.id) === String(distributorId)) ||
      distributorOptions.find((d) => String(d.id) === String(distributorId));
    if (match?.aiReason) {
      setSelectedDistributorReason(match.aiReason);
    }
    const distributorEmail = String(match?.email || "").trim();
    if (!distributorEmail) return;

    const distributorKey = String(distributorId);
    const distributorChanged =
      contactEmailSourceDistributorRef.current !== distributorKey;
    if (distributorChanged) {
      contactEmailSourceDistributorRef.current = distributorKey;
      setPoEmail(distributorEmail);
      setErrors((current) =>
        current.poEmail ? { ...current, poEmail: "" } : current
      );
      return;
    }

    // Options finished loading for the same distributor — fill only if empty.
    setPoEmail((prev) => prev || distributorEmail);
  }, [distributorId, distributorOptions, allDistributorOptions]);

  const uniqueWarnings = useMemo(() => {
    const seen = new Set();
    return warnings.filter((warning) => {
      const key = String(warning).trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [warnings]);

  const bannerWarnings = useMemo(
    () => uniqueWarnings.filter((w) => !isFieldAttachedWarning(w)),
    [uniqueWarnings]
  );

  const showHospitalWarning = useMemo(() => {
    if (needsManualHospital && !hospitalId) return true;
    return uniqueWarnings.some((w) =>
      /ship to state|facility name|facility "|select hospital|multiple hospitals|near hospital|not matched in/i.test(
        String(w)
      )
    );
  }, [uniqueWarnings, needsManualHospital, hospitalId]);

  const selectedHospitalState = useMemo(() => {
    if (!hospitalId) return "";
    return String(
      hospitalLookup.get(String(hospitalId))?.stateCode || ""
    )
      .trim()
      .toUpperCase();
  }, [hospitalId, hospitalLookup]);

  const coveringDistributors = useMemo(() => {
    if (!selectedHospitalState) return [];
    return filterDistributorsByState(
      allDistributorOptions,
      selectedHospitalState
    );
  }, [selectedHospitalState, allDistributorOptions]);

  const selectedHospitalName = useMemo(() => {
    return (
      hospitalLookup.get(String(hospitalId))?.name ||
      facilityName ||
      "this hospital"
    );
  }, [hospitalId, hospitalLookup, facilityName]);

  const selectedCoversHospital = useMemo(() => {
    if (!selectedHospitalState || !distributorId) return false;
    return coveringDistributors.some(
      (d) => String(d.id) === String(distributorId)
    );
  }, [selectedHospitalState, distributorId, coveringDistributors]);

  // Warning when this state has no covering VA, or multiple and none picked.
  const showDistributorWarning = useMemo(() => {
    if (!hospitalId) {
      return uniqueWarnings.some((w) =>
        /no distributor|multiple distributors/i.test(String(w))
      );
    }
    if (coveringDistributors.length === 0) return true;
    if (coveringDistributors.length > 1 && !selectedCoversHospital) return true;
    return false;
  }, [
    uniqueWarnings,
    hospitalId,
    coveringDistributors,
    selectedCoversHospital,
  ]);

  const hospitalWarningMessage = useMemo(() => {
    const hit = uniqueWarnings.find((w) =>
      /ship to state|facility name|facility "|select hospital|multiple hospitals|near hospital|not matched in/i.test(
        String(w)
      )
    );
    return hit
      ? String(hit)
      : "Facility not matched for Ship To state — review Suggested or pick manually.";
  }, [uniqueWarnings]);

  const distributorWarningMessage = useMemo(() => {
    const stateLabel = selectedHospitalState || "this state";
    if (
      hospitalId &&
      coveringDistributors.length > 1 &&
      !selectedCoversHospital
    ) {
      return `Multiple distributors cover state ${stateLabel}. Select a distributor manually.`;
    }
    if (hospitalId && coveringDistributors.length === 0) {
      return `No distributor for state ${stateLabel} — showing all distributors.`;
    }
    if (!hospitalId) {
      const none = uniqueWarnings.find((w) =>
        /no distributor/i.test(String(w))
      );
      if (none) return `${none} — select manually.`;
    }
    return `No distributor for state ${stateLabel} — select manually.`;
  }, [
    uniqueWarnings,
    hospitalId,
    coveringDistributors,
    selectedCoversHospital,
    selectedHospitalState,
  ]);
  const showLowConfidenceHint = !isManual && hasLowConfidence(aiMeta);
  const addressIssues = useMemo(
    () => !isManual && !isDeviceOrder ? getPoAddressIssues({
      ...delivery,
      // Editing regenerates delivery.rawShipTo; retain the imported source for comparison.
      rawShipTo: parseData?.delivery?.rawShipTo || delivery.rawShipTo,
    }) : [],
    [isManual, isDeviceOrder, delivery, parseData]
  );
  const showPrescriptionSection =
    !hidePrescriptionSection && bundleRequiresPrescription(bundleCode);
  const { currentData: concernBundleResponse } = useGetVaBundleQuery(bundleCode, {
    skip: isManual || isDeviceOrder || !bundleCode,
  });
  const reviewPrescriptionConcerns = useMemo(() => {
    if (isManual || isDeviceOrder) return [];
    const parsedBundle = parseData?.bundle;
    const sameCode = value => String(value || "").replace(/[^a-z0-9]/gi, "").toUpperCase();
    const bundle = concernBundleResponse?.data ||
      (sameCode(parsedBundle?.code) === sameCode(bundleCode) ? parsedBundle : null);
    return buildReviewPrescriptionConcerns({
      bundle, prescription,
      hasFile: Boolean(prescriptionFile || prescription.fileUrl || prescription.prescriptionFileKey),
    });
  }, [isManual, isDeviceOrder, concernBundleResponse, parseData, bundleCode, prescription, prescriptionFile]);

  const validate = () => {
    const next = {};
    if (!isManual && !isDeviceOrder && !String(poNumber || "").trim()) {
      next.poNumber = "VA PO Number is required";
    }
    if (!isManual && !isDeviceOrder && !/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(poEmail.trim())) {
      next.poEmail = "Enter a valid contact email.";
    }
    if (!isManual && !hospitalId) next.hospital = "Select a hospital";
    if (!distributorId) next.distributor = "Select a distributor";
    const deliveryFieldErrors = getDeliveryFieldErrors(delivery);
    if (Object.keys(deliveryFieldErrors).length > 0) {
      next.delivery = true;
    }
    if (
      showPrescriptionSection &&
      hasBlockingPrescriptionIssue(prescription?.extractMeta?.issues)
    ) {
      next.prescription =
        prescription.extractMeta.issues.find((issue) => issue.blocking)
          ?.message || "Fix prescription issues before confirming";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleHospitalChange = (nextHospitalId) => {
    const id = String(nextHospitalId || "").trim();
    setHospitalId(id);
    const selected = id ? hospitalLookup.get(id) : null;
    setDelivery((prev) => ({
      ...prev,
      hospitalId: id || undefined,
      hospitalName: selected?.name || prev.hospitalName || "",
    }));
    // Reset distributor so effect can auto-pick when exactly one covers.
    setDistributorId("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.hospital;
      return next;
    });
  };

  const getPrescriptionFileForAi = () =>
    prescriptionFile ||
    (parseId ? getSavedPrescriptionFile(parseId)?.file : null);

  const runAiPrescriptionExtraction = async () => {
    const file = getPrescriptionFileForAi();
    if (!file) {
      setRxParseError("Upload a prescription file before AI extraction.");
      return;
    }
    setRxParseError("");
    setGlobalError("");
    try {
      const extracted = await parseRx({
        file,
        parseId: isManual ? null : parseId,
      });
      const nextPrescription = {
        ...extracted,
        fileName: file.name,
        fileUrl: parseId
          ? getSavedPrescriptionFile(parseId)?.fileUrl || prescription.fileUrl
          : prescription.fileUrl,
      };
      setPrescription(nextPrescription);
      if (parseId) {
        const saved = getParseResult(parseId);
        if (saved) {
          saveParseResult(parseId, {
            ...saved,
            prescription: nextPrescription,
            rxParseError: undefined,
          });
        }
      }
    } catch (err) {
      setRxParseError(
        err?.data?.message ||
          "Could not read prescription. Enter values and compare the file."
      );
    }
  };

  const handleAiExtractClick = () => {
    if (isParsingRx) return;
    const file = getPrescriptionFileForAi();
    if (!file) {
      setRxParseError("Upload a prescription file before AI extraction.");
      return;
    }
    setShowAiExtractConfirmModal(true);
  };

  const handleAiExtractConfirm = async () => {
    setShowAiExtractConfirmModal(false);
    await runAiPrescriptionExtraction();
  };

  const handleConfirm = async () => {
    setGlobalError("");
    if (!validate()) return;

    try {
      const selectedDistributor = distributorOptions.find(
        (d) => String(d.id) === String(distributorId)
      );
      // New orders: Order From = facility/hospital name (existing orders keep prior values).
      const orderFrom =
        String(
          hospitalLookup.get(String(hospitalId))?.name || facilityName || ""
        ).trim() || formatDistributorLabel(selectedDistributor);
      // PDF orders retain their supplied Rx even when the purchased bundle has no lenses.
      const includePrescription = showPrescriptionSection || (!isManual && !isDeviceOrder);

      const payload = {
        parseId: isManual ? null : parseId,
        bundleCode,
        distributorId,
        hospitalId: isManual ? undefined : hospitalId,
        orderFrom,
        // Create Order modal fields → confirm API keys (same labels as UI).
        patientName: patientName.trim(),
        providerName: providerName.trim(),
        prescription: includePrescription
          ? prescription
          : { ...EMPTY_PRESCRIPTION },
        prescriptionFile: includePrescription ? prescriptionFile : null,
        prescriptionFileKey: includePrescription
          ? prescription.prescriptionFileKey || null
          : null,
        delivery: {
          ...delivery,
          ...(hospitalId ? { hospitalId } : {}),
        },
        poNumber,
        ...(!isManual && !isDeviceOrder ? { poEmail: poEmail.trim() } : {}),
        originalPoFile: isManual ? null : originalPoFile,
        manual: isManual,
        lensSelection: initialLensSelection,
      };
      const result = isDeviceOrder
        ? (await createDeviceOrder({ ...deviceDraft, review: payload }).unwrap())?.data
        : await confirm(payload);
      const orderId = getConfirmNavigationOrderId(result);
      if (result?.confirmationEmailStatus === "failed") {
        showToast("Order created, but the confirmation email could not be queued. Please contact support; do not submit the order again.", "error");
      }

      // Persist Order From on the order so details page can display it
      if (!isDeviceOrder && orderId && orderFrom) {
        try {
          const response = await getOrders({
            ShopifyId: orderId,
            page: 1,
            limit: 1000,
          }).unwrap();
          const createdOrders = response?.data?.orders || [];
          await Promise.all(
            createdOrders
              .filter((order) => order?.ItemID)
              .map((order) =>
                updateOrder({
                  ItemID: order.ItemID,
                  OrderFrom: orderFrom,
                }).unwrap()
              )
          );
        } catch {
          // Order was created; Order From sync is best-effort
        }
      }

      if (result?.confirmationEmailStatus !== "failed") {
        showToast(result?.prescriptionConcerns?.length
          ? "Order created. Prescription concerns have been recorded for distributor review."
          : "Order created successfully", "success");
      }
      handleClose();
      if (orderId) {
        navigate(`/orders/order-details/${orderId}`);
      } else {
        navigate(AppRoutes.Orders.path);
      }
    } catch (err) {
      setGlobalError(
        err?.data?.message || "Failed to create order. Please try again."
      );
    }
  };

  if (!isManual && !parseData) {
    return asModal ? null : <Loader />;
  }

  const content = (
    <div className="va-create-order">
      <PoAddressIssuesBanner issues={addressIssues} />
      <PrescriptionConcernsBanner concerns={reviewPrescriptionConcerns} />
      {isDeviceOrder && <div className="review-section">
        <strong>VA device: {deviceDraft.macAddress}</strong>
        <p className="mb-0">SN: {deviceDraft.serialNumber}. An internal VA order number will be generated on confirmation.</p>
      </div>}
      {!isManual && bannerWarnings.length > 0 && (
        <div className="va-review-banner va-review-banner--warning mb-3">
          {bannerWarnings.map((warning, index) => (
            <p key={index} className="mb-0 small">
              {warning}
            </p>
          ))}
        </div>
      )}

      {showLowConfidenceHint && (
        <div className="va-review-banner va-review-banner--warning mb-3">
          <p className="mb-0 small">
            Please verify all fields before confirm
          </p>
        </div>
      )}

      <div className="review-section">
        <BundleSelector
          value={bundleCode}
          onChange={setBundleCode}
          skuCode={skuCode}
          bundleFromParse={parseData?.bundle}
          bundleProducts={parseData?.bundleProducts}
        />
      </div>

      {!isManual && !isDeviceOrder && (
        <div className="review-section">
          <VaFileInput
            id="va-original-po-upload"
            label="VA PO File"
            labelClassName="va-review-bold-label"
            allowImages
            accept={PRESCRIPTION_UPLOAD_ACCEPT}
            file={originalPoFile}
            emptyLabel="Optional. PDF, PNG, or JPEG."
            showUploadIcon
            onFileSelect={handleOriginalPoFileSelect}
          />
          {originalPoFile ? (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-danger mt-2"
              onClick={clearOriginalPoFile}
            >
              Remove file
            </button>
          ) : null}
          <div className="mt-3">
            <InputWithLabel
              label="VA PO Number"
              name="poNumber"
              value={poNumber}
              onChange={(e) => {
                setPoNumber(e.target.value);
                setErrors((prev) => {
                  if (!prev.poNumber) return prev;
                  const next = { ...prev };
                  delete next.poNumber;
                  return next;
                });
              }}
              placeholder="Enter VA PO Number"
              className="mb-0"
              labelClassName="va-review-bold-label"
              required
              error={!!errors.poNumber}
            />
            {errors.poNumber ? (
              <p className="text-danger small mb-0 mt-1">{errors.poNumber}</p>
            ) : null}
          </div>
        </div>
      )}

      <div className="review-section">
        {!isManual && (
          <div className="mb-3">
            <OrderHospitalSelect
              value={hospitalId}
              onChange={handleHospitalChange}
              stateGroups={hospitalStateGroups}
              isLoading={hospitalsLoading}
              facilityName={facilityName}
              suggestedHospitals={suggestedHospitals}
              error={!!errors.hospital}
              errorMessage={errors.hospital || ""}
              showWarning={showHospitalWarning}
              warningMessage={hospitalWarningMessage}
            />
          </div>
        )}
        <DistributorSelect
          value={distributorId}
          onChange={setDistributorId}
          options={distributorOptions}
          suggestedOptions={suggestedDistributors}
          showWarning={showDistributorWarning}
          warningMessage={distributorWarningMessage}
          facilityName={facilityName}
          hospitalName={
            hospitalLookup.get(String(hospitalId))?.name || ""
          }
          hospitalId={hospitalId}
          suggestedHospitalIds={suggestedHospitals.map((h) => h.id)}
          error={!!errors.distributor}
        />
        {errors.distributor && (
          <p className="text-danger small mb-0">{errors.distributor}</p>
        )}
        {!isManual && !isDeviceOrder && (
          <div className="mt-3">
            <label
              htmlFor="po-confirmation-email"
              className="cmn_label va-review-bold-label d-block mb-2"
            >
              Contact email <span className="text-danger">*</span>
            </label>
            <input
              id="po-confirmation-email"
              type="email"
              required
              autoComplete="off"
              className={`form-control${errors.poEmail ? " is-invalid" : ""}`}
              value={poEmail}
              disabled
              readOnly
              aria-invalid={Boolean(errors.poEmail)}
              aria-describedby="po-confirmation-email-help"
              placeholder="Distributor contact email"
            />
            <p
              id="po-confirmation-email-help"
              className={`small mt-2 mb-0 ${
                errors.poEmail ? "text-danger" : "text-muted"
              }`}
            >
              {errors.poEmail ||
                "Defaults to the selected distributor email. Order confirmation is sent here."}
            </p>
          </div>
        )}
      </div>

      {showPrescriptionSection && (
        <div className="review-section">
          <PrescriptionSection
            prescription={prescription}
            onChange={(next) => {
              const fileRemoved = Boolean(prescription.fileName) && !next.fileName;
              if (fileRemoved) {
                if (
                  typeof prescription.fileUrl === "string" &&
                  prescription.fileUrl.startsWith("blob:")
                ) {
                  URL.revokeObjectURL(prescription.fileUrl);
                }
                setPrescription({ ...EMPTY_PRESCRIPTION });
                setPrescriptionFile(null);
                setRxParseError("");
                setGlobalError("");
                if (parseId) {
                  clearPrescriptionFile(parseId);
                  const saved = getParseResult(parseId);
                  if (saved) {
                    saveParseResult(parseId, {
                      ...saved,
                      prescription: { ...EMPTY_PRESCRIPTION },
                      prescriptionFileKey: null,
                      rxParseError: undefined,
                    });
                  }
                }
                return;
              }
              setPrescription(next);
              setGlobalError("");
              if (!next.fileName) {
                setPrescriptionFile(null);
                setRxParseError("");
              }
            }}
            onPdTypeChange={(pdType) =>
              setPrescription((p) => ({ ...EMPTY_PRESCRIPTION, ...p, pdType }))
            }
            onFileUpload={async (file) => {
              const fileUrl = URL.createObjectURL(file);
              setPrescriptionFile(file);
              setRxParseError("");
              setGlobalError("");
              if (parseId) savePrescriptionFile(parseId, file);
              setPrescription((p) => ({
                ...EMPTY_PRESCRIPTION,
                ...p,
                fileName: file.name,
                fileUrl,
                // New file — clear prior AI result until user runs extraction
                extractMeta: null,
                prescriptionFileKey: null,
                multiplePrescriptions: false,
              }));
              if (parseId) {
                const saved = getParseResult(parseId);
                if (saved) {
                  saveParseResult(parseId, {
                    ...saved,
                    prescription: {
                      ...(saved.prescription || {}),
                      fileName: file.name,
                      fileUrl:
                        getSavedPrescriptionFile(parseId)?.fileUrl || fileUrl,
                      extractMeta: null,
                      prescriptionFileKey: null,
                    },
                    rxParseError: undefined,
                  });
                }
              }
            }}
            onAiExtract={handleAiExtractClick}
            requiresPrescription={false}
            errors={errors}
            parsingFile={isParsingRx}
            parseError={rxParseError}
          />
        </div>
      )}

      <div className="review-section">
        <DeliverySummary
          delivery={delivery}
          poNumber={poNumber}
          onSave={setDelivery}
          onPoNumberChange={setPoNumber}
          startEditing={
            isManual &&
            !String(delivery?.rawShipTo || "").trim() &&
            !delivery?.address1
          }
          forceValidate={!!errors.delivery}
        />
      </div>

      {!isDeviceOrder && (
        <div className="review-section">
          <div className="va-patient-provider-card">
            <div className="row g-3 va-patient-provider-card__fields">
              <div className="col-md-6">
                <label
                  htmlFor="va-patient-name"
                  className="cmn_label va-review-bold-label d-block"
                >
                  Patient name (optional)
                </label>
                <input
                  id="va-patient-name"
                  type="text"
                  className={`form-control${
                    errors.patientName ? " is-invalid" : ""
                  }`}
                  value={patientName}
                  onChange={(event) => {
                    setPatientName(event.target.value);
                    setErrors((current) => {
                      if (!current.patientName) return current;
                      const next = { ...current };
                      delete next.patientName;
                      return next;
                    });
                  }}
                  placeholder="Patient name"
                  disabled={isSubmitting}
                  aria-invalid={Boolean(errors.patientName)}
                />
                {errors.patientName ? (
                  <p className="text-danger small mb-0 mt-1">
                    {errors.patientName}
                  </p>
                ) : null}
              </div>
              <div className="col-md-6">
                <label
                  htmlFor="va-provider-name"
                  className="cmn_label va-review-bold-label d-block"
                >
                  Provider name (optional)
                </label>
                <input
                  id="va-provider-name"
                  type="text"
                  className={`form-control${
                    errors.providerName ? " is-invalid" : ""
                  }`}
                  value={providerName}
                  onChange={(event) => {
                    setProviderName(event.target.value);
                    setErrors((current) => {
                      if (!current.providerName) return current;
                      const next = { ...current };
                      delete next.providerName;
                      return next;
                    });
                  }}
                  placeholder="Provider name"
                  disabled={isSubmitting}
                  aria-invalid={Boolean(errors.providerName)}
                />
                {errors.providerName ? (
                  <p className="text-danger small mb-0 mt-1">
                    {errors.providerName}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {globalError && <p className="text-danger small">{globalError}</p>}

      <p className="va-ai-disclaimer mt-3 mb-0">
        AI can make mistakes. Please check important information before
        confirming.
      </p>
    </div>
  );

  const aiExtractConfirmModal = (
      <CommonModal
        show={showAiExtractConfirmModal}
        setShow={setShowAiExtractConfirmModal}
        title="Run AI Extraction?"
        centered
        className="modal-sm"
        confirmLabel="Yes, fill from file"
        cancelLabel="Cancel"
        confirmLoading={isParsingRx}
        confirmDisabled={isParsingRx}
        onConfirm={handleAiExtractConfirm}
        onCancel={() => setShowAiExtractConfirmModal(false)}
      >
        <div className="commonBT pt-3 pb-3">
          <p className="mb-2">
            AI will read the prescription file and fill in the eye numbers
            (SPH, CYL, AXIS, ADD, and PD).
          </p>
          <p className="mb-0">
            If any of those fields already have values, they will be
            overwritten with what AI finds. You can still check and edit
            everything before you confirm the order.
          </p>
        </div>
      </CommonModal>
  );

  if (asModal) {
    const confirmDisabled =
      isSubmitting ||
      isParsingRx ||
      (showPrescriptionSection &&
        hasBlockingPrescriptionIssue(prescription?.extractMeta?.issues));

    const modalBody = hasInvoicePreview ? (
      <div className="create-order-split">
        <InvoicePreviewPane
          fileName={savedInvoice?.name}
          fileType={savedInvoice?.type || savedInvoice?.file?.type}
          fileUrl={savedInvoice?.fileUrl}
        />
        <div className="create-order-split__form">
          <div className="create-order-split__form-header">
            <h4 className="create-order-split__title mb-0">Create order</h4>
            <button
              type="button"
              className="common-modal-close btn btn-link p-0 border-0"
              onClick={handleClose}
              aria-label="Close"
            >
              <CancelSvg />
            </button>
          </div>
          <div className="create-order-split__form-body">{content}</div>
          <div className="create-order-split__actions d-flex gap-3">
            <Button
              label="Confirm"
              size="lg"
              onClick={handleConfirm}
              loading={isSubmitting || isParsingRx}
              disabled={confirmDisabled}
            />
            <Button
              label="Cancel"
              size="lg"
              className="cancel"
              onClick={handleClose}
              disabled={isSubmitting || isParsingRx}
            />
          </div>
        </div>
      </div>
    ) : (
      content
    );

    return (
      <>
        <CommonModal
          show
          setShow={(open) => {
            if (!open) handleClose();
          }}
          title={hasInvoicePreview ? "" : "Create order"}
          centered
          className={`${hasInvoicePreview ? "create-order-with-invoice" : "modal-lg"}${
            showAiExtractConfirmModal ? " create-order-modal--blurred" : ""
          }`}
          closeOnBackdrop={false}
          onConfirm={hasInvoicePreview ? undefined : handleConfirm}
          onCancel={handleClose}
          confirmLoading={isSubmitting || isParsingRx}
          confirmDisabled={confirmDisabled}
          cancelDisabled={isSubmitting || isParsingRx}
          footer={hasInvoicePreview ? null : undefined}
        >
          {modalBody}
        </CommonModal>
        {aiExtractConfirmModal}
      </>
    );
  }

  return (
    <>
    <div className="admin-page-card mx-auto" style={{ maxWidth: 720 }}>
      <h4 className="mb-4">Create order</h4>
      {content}
      <div className="d-flex gap-2 mt-3">
        <Button
          label="Confirm"
          onClick={handleConfirm}
          loading={isSubmitting}
          disabled={
            isSubmitting ||
            isParsingRx ||
            (showPrescriptionSection &&
              hasBlockingPrescriptionIssue(prescription?.extractMeta?.issues))
          }
        />
        <Button
          label="Cancel"
          className="cancel"
          onClick={handleClose}
          disabled={isSubmitting || isParsingRx}
        />
      </div>
    </div>
    {aiExtractConfirmModal}
    </>
  );
}
